/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Input as AntInput, InputRef } from 'antd'
import React, { useState, forwardRef } from 'react'
import { ErrorLabel } from '../ErrorLabel'
import Text from '../Text'
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'

type PasswordTextInputProps = {
  name: string
  value: string
  setValue: (value: string) => void
  fieldErrors: any
  customError: any
  label: string
  maxLength?: number
  disabled?: boolean
  autoComplete?: 'new-password' | 'current-password'
  onBlur?: () => void
  placeholder?: string
  style?: any
  tabIndex?: number
  success?: boolean
  error?: boolean
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

export const PasswordTextInput = forwardRef<InputRef, PasswordTextInputProps>(
  (
    {
      name,
      value,
      setValue,
      fieldErrors,
      customError,
      label,
      maxLength = 255,
      disabled = false,
      autoComplete = 'current-password',
      onBlur,
      placeholder,
      style,
      tabIndex,
      success = false,
      error = false,
      onKeyDown,
    },
    ref
  ) => {
    const [passwordVisible, setPasswordVisible] = useState(false)

    const suffixIcon = (
      <span
        onClick={() => setPasswordVisible(!passwordVisible)}
        css={css`
          cursor: pointer;
          color: ${colors.WHITE};
          display: inline-flex;
          align-items: center;
        `}
      >
        {passwordVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
      </span>
    )

    const successIcon = success ? (
      <span
        css={css`
          color: ${colors.SUCCESS_300};
        `}
      >
        <CheckCircleOutlined />
      </span>
    ) : null

    const errorIcon = error ? (
      <span
        css={css`
          color: ${colors.ERROR_300};
        `}
      >
        <CloseCircleOutlined />
      </span>
    ) : null

    const finalSuffix = errorIcon || successIcon || suffixIcon

    return (
      <>
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          {label}
        </Text>
        <AntInput
          ref={ref}
          type={passwordVisible ? 'text' : 'password'}
          placeholder={placeholder}
          status={
            fieldErrors[name] ||
            (Array.isArray(fieldErrors) && fieldErrors?.includes(name))
              ? 'error'
              : ''
          }
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={maxLength}
          disabled={disabled}
          autoComplete={autoComplete}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          suffix={finalSuffix}
          tabIndex={tabIndex}
          css={css`
            font-size: 14px !important;
            height: 40px !important;
            border-radius: 8px;
            border: 1px solid
              ${error
                ? colors.ERROR_300
                : success
                ? colors.SUCCESS_300
                : colors.SECONDARY_100};
            background: ${colors.SECONDARY_200};
            ${success &&
            !error &&
            css`
              border: 1px solid ${colors.SUCCESS_300} !important;
              box-shadow: 0 0 0 2px rgba(82, 196, 26, 0.2) !important;
            `}
            ${error &&
            css`
              border: 1px solid ${colors.ERROR_300} !important;
              box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
            `}
            ${disabled &&
            css`
              color: ${colors.NEUTRAL_100};
            `}
            ${!disabled &&
            css`
              color: ${colors.WHITE};
            `}
            ${style}
          `}
        />
        <ErrorLabel
          fieldErrors={fieldErrors}
          customError={customError}
          name={name}
        />
      </>
    )
  }
)
